
import React from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { Button } from "@/components/ui/button";
import LocationHero from '@/components/locations/LocationHero';
import LocationFeatures from '@/components/locations/LocationFeatures';
import LocationTestimonials from '@/components/locations/LocationTestimonials';
import LocationCTA from '@/components/locations/LocationCTA';
import { Helmet } from 'react-helmet-async';
import { trackPhoneCallConversion } from '@/utils/trackPhoneCall';

const EmdrupLocation = () => {
  const testimonials = [
    {
      name: "Kasper Hansen",
      location: "Emdrup",
      text: "Min datter har fået en fantastisk udvikling hos Oscar. Hans pædagogiske tilgang gør, at hun altid glæder sig til klavertimerne.",
      stars: 5
    },
    {
      name: "Lise Jensen",
      location: "Emdrup",
      text: "Som voksen nybegynder var jeg nervøs for at starte på klaver, men Oscar har en helt særlig evne til at motivere. Nu spiller jeg hver dag!",
      stars: 5
    },
    {
      name: "Morten Andersen",
      location: "Bispebjerg",
      text: "Oscar formår at kombinere det tekniske med det kreative på en måde, der gør hver time både lærerig og sjov. Klart den bedste klaverlærer mine børn har haft.",
      stars: 5
    }
  ];

  const features = [
    {
      title: "Let adgang fra Emdrup",
      description: "Undervisningen foregår i Søborg med nem transport fra Emdrup.",
      icon: "map-pin"
    },
    {
      title: "Fleksible tider",
      description: "Undervisningstider tilpasset både børn, studerende og arbejdende voksne.",
      icon: "clock"
    },
    {
      title: "Personlig udvikling",
      description: "Undervisning der fokuserer på din personlige musikalske rejse.",
      icon: "user"
    },
    {
      title: "Alle aldersgrupper",
      description: "Undervisning for både børn fra 6 år, unge og voksne i alle aldre.",
      icon: "users"
    }
  ];

  return (
    <Layout>
      <Helmet>
        <title>Klaverundervisning i Emdrup | Få en gratis prøvetime!</title>
        <meta name="description" content="Professionel klaverundervisning i Emdrup med Oscar Gilbert. Book en gratis prøvetime og start din musikalske rejse i dag!" />
      </Helmet>
      
      <LocationHero 
        title="Klaverundervisning i Emdrup"
        description="Få professionel klaverundervisning med Oscar Gilbert, tilgængelig for elever fra Emdrup. Alle niveauer og aldersgrupper er velkomne."
      />
      
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-serif font-bold mb-6">
                Klaverundervisning fra <span className="text-piano-gold">Emdrup</span>
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                Oscar Gilbert tilbyder professionel klaverundervisning til elever fra Emdrup-området. Med udgangspunkt i dit personlige niveau og dine musikalske mål får du undervisning, der er skræddersyet til netop dig.
              </p>
              <p className="text-lg text-gray-700 mb-6">
                Uanset om du er nybegynder, der aldrig har rørt et klaver før, eller en øvet pianist, der ønsker at forbedre din teknik, tilbyder Oscar en engageret og kompetent undervisning baseret på mange års erfaring.
              </p>
              <p className="text-lg text-gray-700 mb-8">
                Undervisningen foregår i Søborg, som er let tilgængelig med offentlig transport fra Emdrup.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button asChild className="bg-piano-gold hover:bg-piano-gold/90 text-white">
                  <Link to="/kontakt">Book gratis prøvetime</Link>
                </Button>
                <Button asChild variant="outline">
                  <Link to="/undervisning">Se undervisningstilbud</Link>
                </Button>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-piano-gold to-amber-400 rounded-lg blur opacity-20"></div>
              <div className="relative bg-white p-3 rounded-lg shadow-xl">
                <div className="aspect-square bg-gray-100 rounded-md overflow-hidden">
                  <img 
                    src="/lovable-uploads/891ee3d3-fc7a-4f1b-925d-4c77de27f6ae.png" 
                    alt="Klaverundervisning i Emdrup" 
                    className="w-full h-full object-cover object-center object-[50%_35%]"
                    width="400"
                    height="400"
                    loading="eager"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <LocationFeatures features={features} />
      
      <LocationTestimonials testimonials={testimonials} locationName="Emdrup" />
      
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-serif font-bold mb-4">Find vej til undervisningen</h2>
              <p className="text-lg text-gray-600">
                Klaverundervisningen foregår på Niels Finsens Alle 86, 2860 Søborg, med nem adgang fra Emdrup.
              </p>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-xl mb-10">
              <div className="overflow-hidden rounded-md">
                <iframe 
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d17976.406912891856!2d12.516746403857416!3d55.72286821314392!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x465251f89eae19fd%3A0x1029a319978e54b7!2sNiels%20Finsens%20Alle%2086%2C%202860%20S%C3%B8borg!5e0!3m2!1sda!2sdk!4v1743770871988!5m2!1sda!2sdk" 
                  width="100%" 
                  height="450" 
                  style={{ border: 0 }} 
                  allowFullScreen={true} 
                  loading="lazy" 
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Lokation på kort">
                </iframe>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-xl">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <h3 className="text-xl font-serif font-semibold">Adresse</h3>
                  <p className="text-gray-700">
                    Niels Finsens Alle 86<br />
                    2860 Søborg
                  </p>
                  <div className="space-y-2">
                    <p className="text-gray-700"><strong>Transport fra Emdrup:</strong></p>
                    <p className="text-gray-700">• Med buslinje 6A</p>
                    <p className="text-gray-700">• 15 min. fra Emdrup Station</p>
                    <p className="text-gray-700">• Gratis parkering ved undervisningslokalet</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <h3 className="text-xl font-serif font-semibold">Kontakt</h3>
                  <p className="text-gray-700">
                    Telefon: <a href="tel:+4540817127" className="text-piano-gold hover:underline" onClick={trackPhoneCallConversion}>+45 40 81 71 27</a>
                  </p>
                  <p className="text-gray-700">
                    Email: <a href="mailto:kontakt@pianoundervisning.dk" className="text-piano-gold hover:underline">kontakt@pianoundervisning.dk</a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <LocationCTA locationName="Emdrup" />
    </Layout>
  );
};

export default EmdrupLocation;
